import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import useClock from "./hooks/useClock";
import { getNow } from "./utils/shared/time";
import { useEvents } from "./context/EventsContext";
import backArrow from "../assets/backArrow.svg";
import MarketingButton from "./components/MarketingButton";
import { getHeaders } from "./utils/shared/headers";
import { BASE_URL } from "./constants/general";
import { fetchAndCacheState } from "../Upshow";
import SettingsService from "../services/SettingsService";
import "./epg.scss";

const everpassLogo = `https://cdn.upshow.tv/ui-assets/EverPass_Horizontal_All_White.png`;

const BackButton = ({ goBack }) => {
    const { ref, focused } = useFocusable({
        focusKey: "BACK_BUTTON",
        onEnterPress: goBack,
    });

    return (
        <button ref={ref} className={`backBtn ${focused ? "focused" : ""}`} onClick={goBack} aria-label="Back" tabIndex={0}>
            <img src={backArrow} alt="Back" />
        </button>
    );
};

const TestStreamButton = ({ isSubscribed, onToggle, isLoading }) => {
    const { ref, focused } = useFocusable({
        focusKey: "TEST_STREAM_BUTTON",
        onEnterPress: onToggle,
    });
    return (
        <button ref={ref} className={`testStreamButton ${focused ? "isFocused" : ""}`} onClick={onToggle} disabled={isLoading} tabIndex={0}>
            {isSubscribed ? "Stop Test Stream" : "Play Test Stream"}
        </button>
    );
};

const EPGHeader = ({ goBack, closeEPG, showMarketingButton }) => {
    const [currentTime, setCurrentTime] = useState(getNow());
    const [isSubscribedToTestStream, setIsSubscribedToTestStream] = useState(
        () => window.__UpshowState?.isSubscribedToTestStream ?? false
    );
    const [isTestStreamLoading, setIsTestStreamLoading] = useState(false);
    const { showFiltered } = useEvents();

    useClock(setCurrentTime);

    useEffect(() => {
        const handleStateUpdate = (event) => {
            setIsSubscribedToTestStream(event.detail?.isSubscribedToTestStream ?? false);
        };
        window.addEventListener("UpshowStateUpdate", handleStateUpdate);
        return () => window.removeEventListener("UpshowStateUpdate", handleStateUpdate);
    }, []);

    const handleTestStream = useCallback(async () => {
        if (isTestStreamLoading) return;
        const action = isSubscribedToTestStream ? "unsubscribe" : "subscribe";
        const newValue = !isSubscribedToTestStream;

        setIsSubscribedToTestStream(newValue);
        if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = newValue;

        setIsTestStreamLoading(true);
        try {
            await axios.post(`${BASE_URL}/v6/${action}_test_stream`, {}, { headers: getHeaders() });
            fetchAndCacheState();
            closeEPG();
        } catch (err) {
            console.error("Test stream toggle error:", err);
            setIsSubscribedToTestStream(isSubscribedToTestStream);
            if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = isSubscribedToTestStream;
        } finally {
            setIsTestStreamLoading(false);
        }
    }, [isTestStreamLoading, isSubscribedToTestStream, closeEPG]);

    const displayTime = useMemo(() => currentTime.format("hh:mm A"), [currentTime]);
    const betaEnabled = SettingsService.hasFeature('beta');

    const rightSection = useMemo(
        () => (
            <div className="rightButtons">
                {showMarketingButton && <MarketingButton closeEPG={closeEPG} />}
                {betaEnabled && <TestStreamButton isSubscribed={isSubscribedToTestStream} onToggle={handleTestStream} isLoading={isTestStreamLoading} />}
                <h2>{displayTime}</h2>
            </div>
        ),
        [showMarketingButton, displayTime, betaEnabled, isSubscribedToTestStream, handleTestStream, isTestStreamLoading]
    );

    return (
        <div className="headerInfo">
            {showFiltered ? <BackButton goBack={goBack} /> : <img src={everpassLogo} alt="EverPass" />}
            {rightSection}
        </div>
    );
};

export default EPGHeader;
